import { useEffect, useState } from 'react';
import moment from 'moment';
import toSentenceCase from '../../../services/toSentenceCase';
import useRankingUsedMetrics from '../../RankingMatrixForm/useRankingUsedMetrics';
import MetricHeaderCell from '../../MetricHeaderCell';
import { NUMBER_CELL } from '../../Grid/constants';

// When grouping by a date field the displayed group value is a formatted label
// (e.g. "W/E 08/05"), so sort chronologically off the raw date we stashed on
// the row rather than alphabetically off the label.
const dateGroupComparator = (
  _valueA: any,
  _valueB: any,
  nodeA?: any,
  nodeB?: any,
) => {
  const a = moment.utc(nodeA?.data?.['raw-date']).valueOf();
  const b = moment.utc(nodeB?.data?.['raw-date']).valueOf();
  return a - b;
};

const useColDefs = (gadget: VisualisationDefinitions.RankingMatrix) => {
  const usedMetrics = useRankingUsedMetrics(gadget.metrics);
  const [colDefs, setColDefs] = useState<MatrixColDef[]>([]);

  useEffect(() => {
    if (usedMetrics.length > 0) {
      const newColDefs = [] as MatrixColDef[];
      const isFitContent = gadget.gridSizing === 'fitContent';
      const groupDef = {
        headerName: toSentenceCase(gadget.groupByField),
        headerTooltip: gadget.groupByField,
        field: gadget.groupByField,
        cellRenderer: 'groupingCellRenderer',
        suppressHeaderMenuButton: true,
        isLeftAlign: true,
        suppressMovable: true,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        flex: isFitContent ? undefined : 2,
        sort:
          gadget.sorting.mode === 'alphabetical'
            ? gadget.sorting.order
            : undefined,
        comparator: gadget.interval ? dateGroupComparator : undefined,
      };
      newColDefs.push(groupDef);

      gadget.metrics.forEach((rankingMetric) => {
        const actualMetric = usedMetrics.find(
          (m) => m.id === rankingMetric.metricId,
        );
        if (!actualMetric) {
          return;
        }

        const cellRenderer = (() => {
          if (actualMetric.formatting.postfix === 'percentage') {
            return 'percentageCell';
          } else if (actualMetric.formatting.prefix === 'currency') {
            return 'currencyCell';
          }

          return NUMBER_CELL;
        })();

        const displayName = rankingMetric.displayName
          ? rankingMetric.displayName
          : actualMetric.name;
        newColDefs.push({
          headerName: displayName,
          headerTooltip: displayName,
          headerComponent: MetricHeaderCell,
          headerComponentParams: {
            metric: actualMetric,
            rightAlign: true,
            alias: displayName,
          },
          field: rankingMetric.metricId,
          cellRenderer,
          cellRendererParams: {
            fieldView: undefined,
            metric: actualMetric,
          },
          suppressHeaderMenuButton: true,
          headerClass: 'rightAlignedHeader',
          minWidth: isFitContent ? displayName.length * 9 : undefined,
          flex: isFitContent ? undefined : 1,
          wrapHeaderText: true,
          autoHeaderHeight: true,
          sort:
            gadget.sorting.mode === 'metric' &&
            gadget.sorting.metricId === rankingMetric.metricId
              ? gadget.sorting.order
              : undefined,
        });

        if (rankingMetric.isRankingEnabled) {
          newColDefs.push({
            headerName: `${displayName} (Rank)`,
            headerTooltip: `${displayName} (Rank)`,
            headerComponent: MetricHeaderCell,
            headerComponentParams: {
              metric: actualMetric,
              rightAlign: true,
              alias: 'Rank',
            },
            field: `${rankingMetric.metricId}_rank`,
            cellRenderer: NUMBER_CELL,
            suppressHeaderMenuButton: true,
            headerClass: 'rightAlignedHeader',
            minWidth: isFitContent ? displayName.length * 9 : undefined,
            flex: isFitContent ? undefined : 1,
            wrapHeaderText: true,
            autoHeaderHeight: true,
          });
        }

        if (rankingMetric.isPercentageOfTotalEnabled) {
          newColDefs.push({
            headerName: `${displayName} (% of total)`,
            headerTooltip: `${displayName} (% of total)`,
            headerComponent: MetricHeaderCell,
            headerComponentParams: {
              metric: actualMetric,
              rightAlign: true,
              alias: '% of total',
            },
            field: `${rankingMetric.metricId}_percentage`,
            cellRenderer: 'percentageCell',
            cellRendererParams: {
              fieldView: undefined,
              metric: actualMetric,
              precisionOverride: rankingMetric.percentageOfTotalPrecision,
            },
            suppressHeaderMenuButton: true,
            headerClass: 'rightAlignedHeader',
            minWidth: isFitContent ? displayName.length * 9 : undefined,
            flex: isFitContent ? undefined : 1,
            wrapHeaderText: true,
            autoHeaderHeight: true,
          });
        }
      });

      setColDefs(newColDefs);
    }
  }, [
    gadget.gridSizing,
    gadget.groupByField,
    gadget.interval,
    gadget.metrics,
    gadget.sorting.metricId,
    gadget.sorting.mode,
    gadget.sorting.order,
    usedMetrics,
  ]);

  return {
    colDefs,
  };
};

export default useColDefs;
